import { useState } from 'react';
import {
  Wallet as WalletIcon, ExternalLink, CheckCircle2, Coins, Copy,
  ShieldCheck, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Card } from '../../new-components/ui/card';
import { Button } from '../../new-components/ui/button';
import LoadingSpinner from '../../UI/LoadingSpinner';
import { useWallet } from '../../../contexts/WalletContext';
import { WEB3_CONFIG, getExplorerAddressUrl } from '../../../services/web3/config';
import { TENANT } from '../../../config';
import { SETTINGS_PANEL, SectionHeader, PRIMARY_BTN } from '../ui';

/**
 * Crypto Wallet — self-contained Settings section. Owns the wallet connect/disconnect/token
 * actions via WalletContext. The section title lives on the SettingsDetail page (<h1>).
 */
export function WalletSection() {
  const { isConnected, address, connect, disconnect, chainId, isLoading: walletLoading } = useWallet();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [securityOpen, setSecurityOpen] = useState(false);

  const handleConnectWallet = async () => {
    setError(null);
    try {
      await connect();
    } catch (err) {
      console.error('Failed to connect wallet:', err);
      setError('Could not connect your wallet. Please try again.');
    }
  };

  const handleDisconnectWallet = () => {
    disconnect();
    setError(null);
    setNotice(null);
  };

  const handleViewOnExplorer = () => {
    if (!address) {
      setError('No wallet connected.');
      return;
    }
    window.open(getExplorerAddressUrl(address), '_blank', 'noopener,noreferrer');
  };

  const handleCopyAddress = async () => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      setNotice('Address copied to clipboard.');
    } catch {
      setError('Could not copy the address.');
    }
  };

  const handleAddTokenToWallet = async () => {
    if (!address) {
      setError('Please connect your wallet first.');
      return;
    }
    const contractAddress = WEB3_CONFIG.PROPERTY_TOKEN_ADDRESS;
    try {
      await navigator.clipboard.writeText(contractAddress);
      setNotice(`Contract address copied (${contractAddress}). PropertyToken (ERC1155) tokens appear in your wallet automatically when received.`);
    } catch (err) {
      console.error('Error adding token to wallet:', err);
      setNotice(`PropertyToken contract: ${contractAddress} — add this manually in your wallet's "Import Tokens" section.`);
    }
  };

  const getChainName = (id: number | undefined) => {
    if (!id) return 'Unknown';
    switch (id) {
      case 1: return 'Ethereum Mainnet';
      case 1440002: return 'XRP EVM Sidechain';
      case 11155111: return 'Sepolia Testnet';
      case 5: return 'Goerli Testnet';
      default: return `Chain ID: ${id}`;
    }
  };

  const formatAddress = (addr: string | undefined) => (addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : '');

  const inlineError = error && (
    <p className="text-sm text-destructive">{error}</p>
  );

  return (
    <Card className="p-6 border-border">
      <p className="mb-6 text-sm text-muted-foreground">
        Connect your Web3 wallet to interact with smart contracts and manage property tokens.
      </p>

      {!isConnected ? (
        <div className="space-y-6">
          {/* Connect */}
          <div className={`${SETTINGS_PANEL} p-8 text-center`}>
            <WalletIcon className="mx-auto mb-4 h-14 w-14 text-muted-foreground" aria-hidden="true" />
            <h3 className="mb-2 text-lg font-semibold text-foreground">Connect your wallet</h3>
            <p className="mx-auto mb-6 max-w-md text-sm text-muted-foreground">
              Link your MetaMask, WalletConnect, or other Web3 wallet to unlock blockchain features
            </p>
            <Button onClick={handleConnectWallet} disabled={walletLoading} className={`gap-2 ${PRIMARY_BTN}`}>
              {walletLoading ? (
                <><LoadingSpinner size="sm" /> Connecting…</>
              ) : (
                <><WalletIcon className="h-4 w-4" /> Connect wallet</>
              )}
            </Button>
            {error && <div className="mt-3">{inlineError}</div>}
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className={`${SETTINGS_PANEL} p-4`}>
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-secondary">
                  <CheckCircle2 className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                </div>
                <div>
                  <h4 className="mb-1 text-sm font-semibold text-foreground">Property token ownership</h4>
                  <p className="text-sm text-muted-foreground">Hold and manage eligible property tokens (ERC1155) directly in your wallet</p>
                </div>
              </div>
            </div>
            <div className={`${SETTINGS_PANEL} p-4`}>
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-secondary">
                  <Coins className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                </div>
                <div>
                  <h4 className="mb-1 text-sm font-semibold text-foreground">Stake for dividends</h4>
                  <p className="text-sm text-muted-foreground">Stake tokens in smart contracts to earn quarterly dividend distributions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Connected */}
          <div className={`${SETTINGS_PANEL} p-5`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 items-start gap-4">
                <CheckCircle2 className="mt-0.5 h-6 w-6 flex-shrink-0 text-[hsl(var(--success))]" aria-hidden="true" />
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-foreground">Wallet connected</h3>
                  <p className="mt-0.5 text-sm text-muted-foreground">Your wallet is ready for transactions</p>
                  <div className="mt-3 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-foreground">Address</span>
                      <code className="rounded-md border border-border bg-background px-2 py-1 font-mono text-sm text-foreground">
                        {formatAddress(address)}
                      </code>
                      <button onClick={handleCopyAddress} aria-label="Copy address" className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-md text-muted-foreground transition-colors md:h-9 md:w-9 md:hover:bg-muted" title="Copy address">
                        <Copy className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-foreground">Network</span>
                      <span className="inline-flex items-center rounded-full bg-[hsl(var(--success))]/10 px-2.5 py-0.5 text-xs font-medium text-[hsl(var(--success))]">
                        {getChainName(chainId)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <Button variant="destructive" onClick={handleDisconnectWallet} size="sm" className="flex-shrink-0 self-start sm:self-auto">
                Disconnect
              </Button>
            </div>
          </div>

          {/* Quick actions */}
          <div>
            <SectionHeader title="Quick actions" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Button variant="outline" onClick={handleViewOnExplorer} className="justify-center gap-2">
                <ExternalLink className="h-4 w-4" /> View on explorer
              </Button>
              <Button variant="outline" onClick={handleAddTokenToWallet} className="justify-center gap-2">
                <WalletIcon className="h-4 w-4" /> Add token to wallet
              </Button>
            </div>
            {notice && <p className="mt-3 break-words text-sm text-muted-foreground">{notice}</p>}
            {error && <div className="mt-3">{inlineError}</div>}
          </div>
        </div>
      )}

      {/* Security & tips — collapsible */}
      <div className={`${SETTINGS_PANEL} mt-6 overflow-hidden`}>
        <button
          type="button"
          onClick={() => setSecurityOpen((o) => !o)}
          aria-expanded={securityOpen}
          className="flex w-full items-center justify-between gap-3 p-4 text-left"
        >
          <span className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="text-sm font-medium text-foreground">Security &amp; tips</span>
          </span>
          {securityOpen
            ? <ChevronUp className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
            : <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />}
        </button>
        {securityOpen && (
          <div className="border-t border-border p-4 rise-in">
            <p className="text-sm text-muted-foreground">
              Always verify transaction details before signing. {TENANT.branding.name} will never ask for your private keys or seed phrase.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

export default WalletSection;
